import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import FAQSection from '../../components/FAQSection'

export default function QuadBiking() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1575550590262-4f1f72a5163f?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Quad Biking Adventures
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Explore Uganda's adventure capital on thrilling ATV trails through villages and along the Nile
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(156 reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-shield-halved" className="w-4 h-4 mr-2" />
                Safety Training
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-users" className="w-4 h-4 mr-2" />
                Family Friendly
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />
                Multiple Durations
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Your Quad Adventure
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Experience Jinja's Best Quad Biking Adventures
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Imagine yourself gripping the handlebars of a powerful quad bike, engine rumbling beneath you as you prepare to explore one of Africa's most beautiful regions from an entirely new perspective. Our quad biking adventures in Jinja—operated by the renowned All Terrain Adventures near Bujagali—deliver exactly this combination of adrenaline, cultural immersion, and spectacular scenery. Whether you're a complete beginner or an experienced rider, these carefully designed safaris through villages, farms, forests, and along pristine Nile riverbanks offer unforgettable experiences for the whole family.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Starting from our Bujagali base near Explorers River Camp, you'll embark on guided ATV safaris ranging from 1 to 4 hours, each revealing different facets of rural Ugandan life and natural beauty. Picture yourself powering through lush tea and sugarcane plantations, navigating winding jungle trails, splashing through muddy tracks (especially thrilling during the wet season!), and pausing at secret viewpoints overlooking the legendary Nile. The automatic quad bikes require no special skills—just twist the throttle and steer—making them perfect for first-timers while still delivering plenty of excitement for seasoned riders.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Comprehensive Training & Safety First</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Before every safari, you'll participate in a mandatory 30-minute briefing and training session that covers quad bike controls, essential safety measures, and trail guidelines. Our experienced instructors categorize riders into groups based on ability—beginners, experienced riders, or children—ensuring everyone receives appropriate guidance. You'll practice on easy terrain until you're completely comfortable, and no prior driving or riding experience is required. We provide complete safety gear including light overalls, gumboots, helmets, scarves, goggles, and gloves to keep you protected and comfortable throughout your adventure.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Wet Season: The Mud Madness Experience</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  While quad biking is fantastic year-round, the wet season (March-May and October-November) transforms the experience into what we call "Mud Madness"—an exhilarating adventure through muddy paths where bikes slide across puddles and potholes, adding an extra dimension of thrill. Beyond the excitement, the wet season brings gorgeously lush green vegetation, vibrant countryside views, and the melodious sounds of tropical birds. Don't worry about getting dirty—that's half the fun, and we provide all the protective gear you need!
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Why Choose Our Quad Biking Experience
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-bicycle', title: 'Automatic Quad Bikes', description: 'Easy-to-ride automatic ATVs—no clutch, no gears, just throttle and brake' },
                  { icon: 'fa-users', title: 'Kids Quads Circuits', description: 'Special supervised circuits for children aged 7-12 with smaller bikes' },
                  { icon: 'fa-shield-halved', title: '30-Min Safety Training', description: 'Mandatory briefing on controls, safety measures, and trail guidelines before every ride' },
                  { icon: 'fa-map', title: 'Multiple Safari Options', description: 'Kyabirwa, Explorer, River Nile, Itanda Falls, and Twilight safaris—1 to 4 hours' },
                  { icon: 'fa-droplet', title: 'Wet Season Mud Madness', description: 'March-May & Oct-Nov offer thrilling muddy trails and lush green scenery' },
                  { icon: 'fa-location-dot', title: 'Bujagali Base Location', description: 'Starting from adventure hub near Explorers River Camp with Nile views' },
                  { icon: 'fa-vest', title: 'Complete Safety Gear', description: 'Overalls, helmets, goggles, gumboots, scarves, and gloves all provided' },
                  { icon: 'fa-camera', title: 'Village & Nile Trails', description: 'Authentic cultural encounters through farms, villages, and pristine riverbanks' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Sample 2-Hour Explorer Safari Itinerary
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and can be customized based on your chosen safari length
                  (1-4 hours) and experience level. We offer Kyabirwa (1hr), Explorer (2hrs), River Nile (3hrs), and Itanda Falls (4hrs) safaris.
                </p>
              </div>
              <div className="space-y-6">
                {[
                  { time: '09:00', title: 'Arrival & Check-In', desc: 'Meet at All Terrain Adventures base near Bujagali. Register, sign waivers, and get fitted with safety gear.' },
                  { time: '09:15', title: '30-Minute Training Session', desc: 'Mandatory comprehensive briefing on quad controls, safety protocols, hand signals, and trail etiquette.' },
                  { time: '09:45', title: 'Practice Riding', desc: 'Get comfortable on your automatic quad bike with guided practice on easy terrain before the safari.' },
                  { time: '10:00', title: 'Safari Begins!', desc: 'Set off on your chosen route—through villages, tea/sugarcane plantations, forests, and along the Nile.' },
                  { time: '11:00', title: 'Scenic Nile Viewpoint', desc: 'Pause at secret riverbank location for photos, refreshments, local stories, and nature appreciation.' },
                  { time: '11:30', title: 'Muddy Trail Section', desc: 'Navigate exciting off-road terrain—splash through puddles and feel the thrill! (Especially fun in wet season)' },
                  { time: '11:55', title: 'Return to Base', desc: 'Complete the circuit back to starting point. Clean up, share stories, and receive completion certificate.' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Packing List */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                What to Bring
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>Note:</strong> All safety gear (overalls, helmets, goggles, gumboots, gloves) is provided.
                  Wear clothes you don't mind getting dirty—mud is part of the fun!
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-shirt" className="w-6 h-6" />
                    Clothing
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Clothes you don't mind getting muddy/dirty</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Long pants recommended (denim works well)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Closed shoes/sneakers (boots provided)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Light jacket for wet season rides</span>
                    </li>
                  </ul>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6" />
                    Essentials
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Sunscreen, hat & sunglasses</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Camera/GoPro (waterproof bag recommended)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Water bottle (refreshments provided)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Sense of adventure & fun!</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={[
              { question: 'Do I need previous riding experience?', answer: 'Not at all! Our automatic quad bikes are beginner-friendly. You\'ll receive a complete 30-minute training session covering all controls and safety before starting. Most first-timers feel confident within minutes.' },
              { question: 'What are the age requirements?', answer: 'Solo riders must be 12+ years. We offer special "Kids Quads Circuits" for children aged 7-12 on smaller bikes with close supervision. Younger children (under 7) can ride tandem with an adult trainer.' },
              { question: 'Which safari duration should I choose?', answer: 'Kyabirwa Safari (1 hour) is perfect for beginners. Explorer Safari (2 hours) is our most popular. River Nile Safari (3 hours) offers extensive village interaction. Itanda Falls Safari (4 hours) is for experienced adventure seekers.' },
              { question: 'When is the best time for quad biking?', answer: 'Year-round! However, wet season (March-May, October-November) offers the thrilling "Mud Madness" experience with muddy trails and lush green landscapes. Dry season provides easier riding conditions.' },
              { question: 'What safety gear is provided?', answer: 'We provide complete safety gear: light overalls, gumboots (rubber boots), helmets, protective scarves/bandanas, goggles, and gloves. All equipment meets international safety standards.' },
              { question: 'Can I bring my camera?', answer: 'Absolutely! We recommend waterproof bags for phones/cameras. GoPros with chest mounts work brilliantly. We make scheduled stops at scenic viewpoints specifically for photos.' },
              { question: 'What happens if it rains?', answer: 'Light rain makes it more exciting! We continue with proper gear. Heavy rain or thunderstorms may require rescheduling for safety. Your safety is always our priority.' },
              { question: 'Is there a weight limit?', answer: 'Yes, maximum rider weight is 120kg (265 lbs) for safety and quad bike performance. Riders are categorized by ability for appropriate group assignments.' }
            ]} />

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Jinja Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/whitewater-rafting" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1530866495561-507c9faab2ed?w=800"
                      alt="Whitewater Rafting"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Whitewater Rafting</h3>
                    <p className="text-gray-700 mb-4">Grade 5 rapids for adrenaline seekers.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>

                <Link to="/activities/bungee-jumping" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1534056802274-775c90d06965?w=800"
                      alt="Bungee Jumping"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Bungee Jumping</h3>
                    <p className="text-gray-700 mb-4">44m freefall over the Nile.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 1-4 hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Wet season (Mar-May, Oct-Nov)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Activity Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Easy to Moderate</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group:</strong> Small groups</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 7+ (kids), 12+ (solo)</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Your Quad Adventure
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Automatic quad bike (ATV)",
                    "Complete safety gear (overalls, helmet, goggles, gumboots, gloves)",
                    "30-minute training & briefing",
                    "Professional experienced guide",
                    "Refreshments & water",
                    "Scenic photo stop opportunities"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Transport to Bujagali base",
                    "Personal travel insurance",
                    "Gratuities for guides",
                    "Damage deposit (refundable)",
                    "Additional safari upgrades",
                    "Meals (unless specified)"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Safety
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Weight Limit:</strong> 120kg (265 lbs) maximum.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Training:</strong> Mandatory 30-min session before every ride.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Location:</strong> Bujagali base near Explorers River Camp.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Quad Biking Adventures"
      />
    </div>
  )
}
